import { AnalyticsEvent, EventItem } from '../types'
import { Transport } from './Transport'
import { ConsentStore } from '../consent/ConsentStore'
import { META_EVENT_NAMES, META_CUSTOM_EVENTS } from '../events/metaEventNames'

function getFbq(): ((...args: unknown[]) => void) | null {
  if (typeof window === 'undefined') return null
  return window.fbq ?? null
}

function buildMetaData(event: AnalyticsEvent): Record<string, unknown> | undefined {
  const { name, payload } = event
  const items: EventItem[] = payload.items ?? []

  switch (name) {
    case 'page_view':
      return undefined

    case 'view_item':
      return {
        content_ids: items.map(i => i.id),
        content_name: items[0]?.name,
        content_category: items[0]?.category,
        content_type: 'product',
        value: payload.value,
        currency: payload.currency,
      }

    case 'view_item_list':
      return {
        content_category: payload.list_id,
        content_ids: items.map(i => i.id),
        content_type: 'product',
      }

    case 'search':
      return {
        search_string: payload.search_term,
        content_ids: items.map(i => i.id),
        content_type: 'product',
      }

    case 'add_to_cart':
      return {
        content_ids: items.map(i => i.id),
        content_name: items[0]?.name,
        content_type: 'product',
        value: payload.value,
        currency: payload.currency,
      }

    case 'begin_checkout':
      return {
        content_ids: items.map(i => i.id),
        content_type: 'product',
        num_items: items.reduce((sum, i) => sum + i.quantity, 0),
        value: payload.value,
        currency: payload.currency,
      }

    case 'add_payment_info':
      return {
        content_category: payload.payment_type,
      }

    case 'purchase':
      return {
        content_ids: items.map(i => i.id),
        content_type: 'product',
        num_items: items.reduce((sum, i) => sum + i.quantity, 0),
        value: payload.value,
        currency: payload.currency,
      }

    case 'add_to_wishlist':
      return {
        content_ids: items.map(i => i.id),
        content_name: items[0]?.name,
        content_category: items[0]?.category,
        content_type: 'product',
        value: payload.value,
        currency: payload.currency,
      }

    case 'generate_lead':
    case 'sign_up':
      return undefined

    default:
      return undefined
  }
}

export class MetaPixelTransport implements Transport {
  readonly name = 'MetaPixelTransport'

  private readonly consent: ConsentStore

  constructor(consent: ConsentStore) {
    this.consent = consent
  }

  send(event: AnalyticsEvent): void {
    // Purchase is emitted server-side via Meta CAPI. Never fire it browser-side.
    if (event.name === 'purchase') return

    if (event.consent.ad_storage !== 'granted') return

    const fbq = getFbq()
    if (!fbq) return

    const vendorName = META_EVENT_NAMES[event.name]
    if (!vendorName) return

    const data = buildMetaData(event)
    const dedup = { eventID: event.event_id }

    if (META_CUSTOM_EVENTS.has(event.name)) {
      if (data) {
        fbq('trackCustom', vendorName, data, dedup)
      } else {
        fbq('trackCustom', vendorName, {}, dedup)
      }
    } else {
      if (data) {
        fbq('track', vendorName, data, dedup)
      } else {
        fbq('track', vendorName, {}, dedup)
      }
    }
  }
}
